import React, { Fragment, useState, useMemo } from 'react';
import { FormattedMessage, FormattedNumber, useIntl } from 'react-intl';
import { connect } from 'react-redux';

import { getMaxSelectableInvoiceBalance } from 'components/BatchPayments/helpers';
import InvoiceInfo from 'components/Core/Invoice/Info/InvoiceInfo';
import InvoicePreviewWrapper from 'components/Core/Invoice/Preview/Wrapper/InvoicePreviewWrapper';
import BatchInvoiceInfo from 'components/Core/Invoice/Summary/BatchInvoice/BatchInvoiceInfo';
import ViewInvoiceMessage from 'components/Core/Invoice/ViewInvoiceMessage/ViewInvoiceMessage';
import InvoiceViewer from 'components/Core/Invoice/Viewer/InvoiceViewer';
import PaymentInfo from 'components/Core/Payment/PaymentInfo/PaymentInfo';
import TransactionLineItemList from 'components/PaymentReq/TransactionDetail/TransactionLineItemList';
import DataRow from 'components/Shared/DataRow/DataRow';
import FormattedDateNoTimeZone from 'components/Shared/FormattedDateNoTimeZone/FormattedDateNoTimeZone';
import Hr from 'components/Shared/Hr/Hr';
import { Download, Print, ErrorInfo } from 'components/Shared/Icons/Icons';
import ProgressButton from 'components/Shared/ProgressButton/ProgressButton';
import Separator from 'components/Shared/Separator/Separator';
import SubscriptionDetails from 'components/Subscription/Details/SubscriptionDetails';
import { useIsMobileScreen } from 'hooks/useIsMobile';
import { useRHSExperiment } from 'hooks/useRHSExperiment';
import SegmentIO from 'reporting/SegmentIO';
import { getTxnTextBasedOnType } from 'shared/utils';
import { companyInfoSelectors } from 'store/companyInfo/selectors';
import { insightSelectors } from 'store/insight/selectors';
import { fetchPDFDocument, downloadPDFDocument } from 'store/invoiceDocument/slice';
import { isBatchPaymentsEnabled } from 'store/ixp/selector';
import { modalActions } from 'store/modal/slice';
import { saleSelectors } from 'store/sale/selectors';
import { RootState } from 'store/store';
import { fontSize, colors, breakpoints } from 'styles/cp';
import { CompanyInfo } from 'types/CompanyInfo';
import { FeatureFlags } from 'types/FeatureFlags';
import { IXP } from 'types/IXP';
import { Insight } from 'types/Insight';
import { InvoiceDocument } from 'types/InvoiceDocument';
import { LineItem, Sale } from 'types/Sale';
import { TXN_MAP } from 'types/constants';

// Action enum for PDF operations
enum PDF_ACTION {
  DOWNLOAD = 'download',
  VIEW = 'view',
  PRINT = 'print',
}

const { getOneToManyEstimatedDelivery } = require('shared/clientUtils');

export type Props = {
  // Core data from Redux store
  sale: Sale;
  companyInfo: CompanyInfo;
  insight: Insight;
  featureFlags: FeatureFlags;
  ixp: IXP;
  invoiceDocument: InvoiceDocument;
  totalAmount?: number;
  config?: any;
  payment?: any;
  isBatchPaymentsEnabled?: boolean;

  // Actions
  downloadPDFDocument?: (lazyFetch?: boolean) => void;
  showModal?: (modalConfig: {
    component: React.ComponentType<any>;
    componentProps: Record<string, any>;
  }) => void;
  fetchPDFDocument: (token: string) => void;
};

const PreviewCard: React.FC<Props> = (props) => {
  const {
    sale,
    companyInfo,
    insight,
    featureFlags,
    invoiceDocument,
    downloadPDFDocument,
    showModal,
    fetchPDFDocument,
    totalAmount,
    config,
    payment,
    isBatchPaymentsEnabled,
  } = props;

  const intl = useIntl();
  const [downloadViewOrPrintClicked, setDownloadViewOrPrintClicked] = useState(false);

  // Extract data using selectors - much cleaner!
  const companyName = companyInfoSelectors.nameSelector(companyInfo);
  const companyLogoUrl = companyInfoSelectors.logoSelector(companyInfo) || undefined;
  const number = saleSelectors.referenceNumberSelector(sale);
  const currency = saleSelectors.currencySelector(sale);
  const token = insightSelectors.tokenSelector(insight);
  const dueDate = saleSelectors.dueDateSelector(sale);
  const amount = saleSelectors.amountSelector(sale);
  const balanceAmount = saleSelectors.balanceSelector(sale);
  const isPartiallyPaid = insightSelectors.isPartiallyPaidSelector(insight);
  const isPayable = insightSelectors.isPayableSelector(insight);
  const isFullyPaid = insightSelectors.isFullyPaidSelector(insight);
  const lineItems = saleSelectors.lineItemsSelector(sale) as unknown as LineItem[];
  const achOnlineConvenienceFeeAmount = saleSelectors.achOnlineConvenienceFeeAmountSelector(sale);
  const achOnlineConvenienceFeeAmountPaid =
    saleSelectors.achOnlineConvenienceFeeAmountPaidSelector(sale);
  const taxAmount = saleSelectors.taxAmountSelector(sale);
  const expirationDate = sale?.expirationDate;
  const isChangeOrder = saleSelectors.typeSelector(sale) === TXN_MAP.TYPES.CHANGE_ORDER;
  const isSubscription = saleSelectors.isSubscription(sale);
  const linesWithoutOTF = saleSelectors.linesWithoutOTF(sale) as unknown as LineItem[];
  const onlyOTFLines = saleSelectors.onlyOTFLines(sale) as unknown as LineItem[];

  const transactionType = saleSelectors.typeSelector(sale);
  const isInvoice = transactionType === TXN_MAP.TYPES.INVOICE;
  const isEstimate = transactionType === TXN_MAP.TYPES.ESTIMATE;

  const {
    isRHSExperimentControl: isRHSExperimentControlEnabled,
    isRHSExperimentLineItemsEnabled,
    isRHSExperimentBreakdownButtonEnabled,
  } = useRHSExperiment();
  const isMobile = useIsMobileScreen(768);

  // State for breakdown button expand/collapse
  const [isBreakdownExpanded, setIsBreakdownExpanded] = useState<boolean>(false);

  // Determine shouldShowInvoiceLineItems based on mobile-aware treatment
  const shouldShowInvoiceLineItems = useMemo(() => {
    let result = false;
    if (isRHSExperimentControlEnabled) {
      result = false; // Control: keep current experience
    } else if (isRHSExperimentLineItemsEnabled) {
      result = true; // LineItems: show line items directly (includes mobile conversion)
    } else if (isRHSExperimentBreakdownButtonEnabled) {
      result = isBreakdownExpanded; // BreakdownButton: show only when expanded (desktop only)
    }

    return result;
  }, [
    isRHSExperimentControlEnabled,
    isRHSExperimentLineItemsEnabled,
    isRHSExperimentBreakdownButtonEnabled,
    isBreakdownExpanded,
  ]);

  const { fetchPDFStatus } = invoiceDocument || {};
  const notFetched = fetchPDFStatus === null;
  const lazyFetchPDF = () => (notFetched ? fetchPDFDocument(token) : true);

  // Get selected batch invoices from Redux store (no sessionStorage needed!)
  const selectedBatchInvoices = insightSelectors.selectedBatchInvoices(insight);

  // Helper function to render total amount
  const renderTotalAmount = (amount: number, currency: string, showHr: boolean = false) => {
    if (!amount || amount <= 0) return null;

    return (
      <Fragment key="INVOICE_TOTAL">
        {showHr && <Hr marginTop={10} marginBottom={0} borderColor={colors.offWhite} opacity={1} />}
        <DataRow
          idValue="INVOICE_TOTAL"
          defaultMessage="Total"
          bold={true}
          color={colors.black}
          value={<FormattedNumber value={amount} style="currency" currency={currency} />}
        />
      </Fragment>
    );
  };

  const handleAction = (action: PDF_ACTION) => {
    if (isInvoice || isEstimate || isChangeOrder) {
      lazyFetchPDF();
      setDownloadViewOrPrintClicked(true);

      if (action === PDF_ACTION.DOWNLOAD) {
        downloadPDFDocument?.(false);
      }

      if (action === PDF_ACTION.VIEW) {
        showModal?.({
          component: InvoiceViewer,
          componentProps: { closeOnClickOutSide: true, closeOnEsc: false, showCloseButton: true },
        });
      }

      if (action === PDF_ACTION.PRINT) {
        try {
          (document.getElementById('hiddenPDFIframe') as HTMLIFrameElement)?.contentWindow?.print();
        } catch (e) {
          showModal?.({
            component: InvoiceViewer,
            componentProps: { closeOnClickOutSide: true, closeOnEsc: false, showCloseButton: true },
          });
        }
      }

      const documentType = isInvoice ? 'invoice' : isEstimate ? 'estimate' : 'change_order';
      SegmentIO.transactionEngaged({
        activity_type: 'optional_actions',
        ui_action: 'clicked',
        ui_object: `${action}_${documentType}_pdf`,
        ui_object_detail: 'button',
        ui_access_point: 'transaction_flow',
      });
    }
  };

  const sections: React.ReactNode[] = [];

  // Invoice and Payment Request sections
  if (isInvoice || transactionType === TXN_MAP.TYPES.PAYMENT_REQUEST) {
    if (transactionType === TXN_MAP.TYPES.PAYMENT_REQUEST) {
      const txnDate = saleSelectors.txnDateSelector(sale);
      const description = saleSelectors.descriptionSelector(sale);
      const lineItems = saleSelectors.lineItemsSelector(sale);
      const estimatedDelivery = saleSelectors.estimatedDeliverySelector(sale);
      const isOneToMany = saleSelectors.isOneToManyPaymentRequest(sale);

      const paymentRequestSections = [
        !isOneToMany && txnDate && (
          <DataRow
            key="date"
            idValue="PAYMENT_REQUEST_DATE"
            defaultMessage="Request date"
            value={<FormattedDateNoTimeZone value={txnDate} />}
          />
        ),
        description && (
          <div key="desc" style={{ textAlign: 'left', paddingTop: '14px' }}>
            <span
              style={{
                fontSize: '14px',
                display: 'block',
                fontFamily: 'AvenirNextforINTUIT-Medium',
              }}
            >
              {isOneToMany ? (
                <FormattedMessage
                  id="PAYMENT_REQUEST_DESCRIPTION_PRODUCT_SERVICE"
                  defaultMessage="Description of product or service sold"
                />
              ) : (
                <FormattedMessage
                  id="PAYMENT_REQUEST_DESCRIPTION"
                  defaultMessage="Description of goods or services"
                />
              )}
            </span>
            <span
              style={{
                fontSize: '14px',
                whiteSpace: 'pre-line',
              }}
            >
              {description}
            </span>
          </div>
        ),
        lineItems && (
          <div key="items" style={{ textAlign: 'left', paddingTop: '14px' }}>
            <TransactionLineItemList lineItems={[...lineItems]} currency={currency} />
          </div>
        ),
        estimatedDelivery && isOneToMany && (
          <div key="delivery" style={{ textAlign: 'left', paddingTop: '14px' }}>
            <Separator height={20} />
            <span
              style={{
                fontSize: '14px',
                display: 'block',
                fontFamily: 'AvenirNextforINTUIT-Medium',
              }}
            >
              <FormattedMessage
                id="ESTIMATED_DATE_OF_SERVICE_OR_DELIVERY"
                defaultMessage="Estimated date of service or delivery"
              />
            </span>
            <span style={{ fontSize: '14px' }}>
              {getOneToManyEstimatedDelivery(estimatedDelivery, intl)}
            </span>
          </div>
        ),
        renderTotalAmount(amount || 0, currency || 'USD', true),
      ].filter(Boolean);

      sections.push(<Fragment key="payment-request">{paymentRequestSections}</Fragment>);
    } else {
      // Show batch summary when batch payments feature is enabled AND payment is not completed
      // After payment is completed (isFullyPaid), show regular invoice info instead of batch empty state
      const shouldShowBatchSummary = isBatchPaymentsEnabled && !isFullyPaid;

      if (shouldShowBatchSummary) {
        // Calculate max selectable balance for the batch feature
        const maxSelectableBalance = getMaxSelectableInvoiceBalance(
          companyInfo?.region,
          featureFlags,
          config?.amountConfig
        );

        // Calculate partial payment amount for default invoice
        // For single selected invoice, check if it's the default one
        let partialPaymentAmount = null;
        if (selectedBatchInvoices.length === 1 && selectedBatchInvoices[0]?.defaultInvoice) {
          const invoice = selectedBatchInvoices[0];
          const inputAmount = Number(payment?.inputAmount || 0);
          const originalBalance = invoice.balance;

          // Use edited amount if:
          // 1. Input amount is different from original balance (user edited it)
          // 2. Input amount is greater than 0
          // This handles both partial payments (amount < balance) and overpayments (amount > balance)
          if (inputAmount > 0 && inputAmount !== originalBalance) {
            partialPaymentAmount = inputAmount;
          }
        }

        sections.push(
          <BatchInvoiceInfo
            key="batch-invoice"
            batchInvoices={[...selectedBatchInvoices]}
            maxSelectableBalance={maxSelectableBalance}
            partialPaymentAmount={partialPaymentAmount}
          />
        );
      } else {
        sections.push(
          <InvoiceInfo
            key="invoice"
            featureFlags={featureFlags}
            invoiceAmount={amount || 0}
            shouldShowInvoiceLineItems={shouldShowInvoiceLineItems}
            invoiceNumber={number}
            gratuityValue={0}
            invoiceDueDate={dueDate}
            isPartiallyPaid={isPartiallyPaid}
            isFullyPaid={isFullyPaid}
            currency={currency}
            useRedesign={false}
            token={token}
            showModal={showModal}
            invoiceLineItems={lineItems}
            invoiceTaxAmount={taxAmount}
            achOnlineConvenienceFeeAmount={achOnlineConvenienceFeeAmount}
            achOnlineConvenienceFeeAmountPaid={achOnlineConvenienceFeeAmountPaid}
            isBreakdownExpanded={isBreakdownExpanded}
            onToggleBreakdown={() => setIsBreakdownExpanded(!isBreakdownExpanded)}
          />
        );
      }

      // Show payment info (Total and Outstanding balance) when:
      // 1. Regular flow (no batch feature)
      // 2. Batch feature enabled but should not show in batch summary (already shows total there)
      const shouldShowPaymentInfo =
        !!balanceAmount && isPayable && !isFullyPaid && !isBatchPaymentsEnabled;
      const paymentAmount = totalAmount ?? amount;
      if (shouldShowPaymentInfo) {
        sections.push(
          <PaymentInfo
            key="payment"
            amount={paymentAmount}
            balanceAmount={balanceAmount}
            currency={currency}
            CFTooltip={true}
            beforePay={true}
            isBreakdownExpanded={isBreakdownExpanded}
          />
        );
      }
    }
  }

  // Estimate-specific sections
  if (isEstimate || isChangeOrder) {
    const { transaction_text } = getTxnTextBasedOnType(isChangeOrder);

    const estimateRows = [
      { idValue: 'ESTIMATE_NUMBER', defaultMessage: `${transaction_text} number`, value: number },
      expirationDate && {
        idValue: 'EXPIRATION_DATE',
        defaultMessage: 'Expiration date',
        value: <FormattedDateNoTimeZone value={expirationDate} />,
      },
    ].filter(Boolean);

    sections.push(
      <Fragment key="estimate">
        {estimateRows.map((row, idx) => row && <DataRow key={`${row.idValue}-${idx}`} {...row} />)}
        <Hr marginTop={10} marginBottom={0} borderColor={colors.offWhite} opacity={1} />
        <DataRow
          idValue="ESTIMATE_TOTAL_VALUE"
          defaultMessage={`${transaction_text} total`}
          bold={true}
          color={colors.black}
          value={
            currency ? (
              <FormattedNumber value={amount || 0} style="currency" currency={currency} />
            ) : (
              <FormattedNumber value={amount || 0} />
            )
          }
        />
      </Fragment>
    );
  }

  // Subscription-specific sections
  if (isSubscription) {
    const subscriptionSections = [
      <DataRow
        key="details-header"
        regular={true}
        bold={true}
        margin="8px 0px"
        idValue="DETAILS"
        defaultMessage="Details"
        value=""
      />,
      <SubscriptionDetails
        key="subscription-details"
        opened={true}
        currency={currency || 'USD'}
        lines={lineItems || []}
        linesWithoutOTF={linesWithoutOTF}
        onlyOTFLines={onlyOTFLines}
      />,
      taxAmount && taxAmount > 0 && (
        <DataRow
          key="sales-tax"
          regular={true}
          margin="8px 0px"
          idValue={'SALES_TAX'}
          defaultMessage={'Sales Tax'}
          value={<FormattedNumber value={taxAmount} style="currency" currency={currency} />}
        />
      ),
      renderTotalAmount(amount || 0, currency || 'USD', false),
    ].filter(Boolean);

    sections.push(
      <div key="subscription" className="subscription-details">
        {subscriptionSections}
        <style jsx>{`
          .subscription-details {
            margin-top: 28px;

            @media screen and (max-width: ${breakpoints.md}) {
              margin: 28px 0;
            }
          }
        `}</style>
      </div>
    );
  }

  // Action buttons
  if (isInvoice) {
    // 1. Not in breakdown treatment, OR
    // 2. In breakdown treatment AND expanded
    const isGrowthRHSExpandedBreakdown =
      isRHSExperimentBreakdownButtonEnabled && isBreakdownExpanded;
    const isGrowthRHSNotInBreakdownTreatment = !isRHSExperimentBreakdownButtonEnabled;
    const shouldShowGrowthRHSActionButtons =
      isGrowthRHSNotInBreakdownTreatment || isGrowthRHSExpandedBreakdown;

    const shouldShowActionButtons = !isBatchPaymentsEnabled && shouldShowGrowthRHSActionButtons;

    if (shouldShowActionButtons) {
      sections.push(
        <ActionButtons
          key="invoice-actions"
          documentType="invoice"
          transactionText="invoice"
          fetchPDFStatus={fetchPDFStatus}
          downloadViewOrPrintClicked={downloadViewOrPrintClicked}
          shouldShowInvoiceLineItems={shouldShowInvoiceLineItems}
          onAction={handleAction}
        />
      );
    }
  }

  if (isEstimate || isChangeOrder) {
    const { transaction_text } = getTxnTextBasedOnType(isChangeOrder);
    sections.push(
      <ActionButtons
        key="estimate-actions"
        documentType="estimate"
        transactionText={transaction_text}
        fetchPDFStatus={fetchPDFStatus}
        downloadViewOrPrintClicked={downloadViewOrPrintClicked}
        onAction={handleAction}
        includeBottomMargin={true}
      />
    );
  }

  // Error handling
  const error =
    isInvoice && downloadViewOrPrintClicked && fetchPDFStatus === TXN_MAP.STATUS.ERROR ? (
      <Fragment>
        <div>
          <FormattedMessage
            id="ERROR_TEMPORARILY_UNAVAILABLE_INVOICE_REQUEST"
            defaultMessage="Invoice temporarily unavailable. "
          />
          <FormattedMessage
            id="ERROR_GENERIC_APPRECIATE_PATIENCE"
            defaultMessage="We appreciate your patience. "
          />
          <a className="try-again" onClick={() => fetchPDFDocument(token)}>
            <FormattedMessage id="TRY_AGAIN" defaultMessage="Try again" />
          </a>
        </div>
        <style jsx>{`
          .try-again {
            color: ${colors.blue01};
          }
        `}</style>
      </Fragment>
    ) : null;

  return (
    <Fragment>
      <InvoicePreviewWrapper image={companyLogoUrl} title={companyName || ''} isMobile={isMobile}>
        {sections.length > 0 &&
          sections.reduce((acc: React.ReactNode[], section, idx) => {
            if (idx !== 0 && idx < sections.length - 1) {
              acc.push(
                <Hr
                  key={`separator-${idx}`}
                  marginTop={10}
                  marginBottom={10}
                  borderColor={colors.offWhite}
                  opacity={1}
                />
              );
            }
            acc.push(
              <div key={`content-${idx}`} className="card-sides">
                {section}
              </div>
            );
            return acc;
          }, [])}
        {error && (
          <div className="card-sides error flex">
            <div className="icon flex-column">
              <ErrorInfo color={colors.gray02} width={20} height={20} />
            </div>
            <div className="err-msg">{error}</div>
          </div>
        )}
      </InvoicePreviewWrapper>
      <style jsx>{`
        .card-sides {
          padding: 0 0 0 0;
        }
        .err-msg {
          font-size: ${fontSize.xxs};
          padding: 0 0 7px 15px;
        }
        .icon {
          align-self: center;
        }
      `}</style>
    </Fragment>
  );
};

interface ActionButtonsProps {
  documentType: 'invoice' | 'estimate';
  transactionText: string;
  fetchPDFStatus: any;
  downloadViewOrPrintClicked: boolean;
  shouldShowInvoiceLineItems?: boolean;
  onAction: (action: PDF_ACTION) => void;
  includeBottomMargin?: boolean;
}

const ActionButtons: React.FC<ActionButtonsProps> = ({
  documentType,
  transactionText,
  fetchPDFStatus,
  downloadViewOrPrintClicked,
  shouldShowInvoiceLineItems = false,
  onAction,
  includeBottomMargin = false,
}) => {
  const fetchPDFSuccessful = fetchPDFStatus === TXN_MAP.STATUS.SUCCESS;
  const isDownloadingPDF = fetchPDFStatus === TXN_MAP.STATUS.IN_PROGRESS;
  const isEstimateDisabled = false;

  const getButtonColor = () => {
    if (documentType === 'invoice') {
      return fetchPDFSuccessful || !downloadViewOrPrintClicked ? colors.gray : colors.gray04;
    }
    return !isEstimateDisabled || isDownloadingPDF ? colors.gray : colors.gray04;
  };

  const getLeftContent = () => {
    if (documentType === 'invoice') {
      return (
        <ProgressButton
          backgroundColor={colors.white}
          size="mini"
          width="auto"
          onClick={() => onAction(PDF_ACTION.VIEW)}
          disabled={!fetchPDFSuccessful && downloadViewOrPrintClicked}
          spinner={isDownloadingPDF && downloadViewOrPrintClicked}
          spinnerWidth={15}
          data-cy="invoice-summary-view-invoice"
        >
          <ViewInvoiceMessage
            fetchPDFStatus={fetchPDFStatus as any}
            isFull={shouldShowInvoiceLineItems}
            userClicked={downloadViewOrPrintClicked}
          />
        </ProgressButton>
      );
    }

    return (
      <ProgressButton
        backgroundColor={colors.white}
        size="mini"
        width="auto"
        onClick={() => onAction(PDF_ACTION.VIEW)}
        disabled={isEstimateDisabled}
        spinner={isDownloadingPDF}
        spinnerWidth={15}
        data-cy="invoice-summary-view-estimate"
        data-testid="view-estimate-button"
      >
        <FormattedMessage
          id="VIEW_ESTIMATE"
          defaultMessage="View {transaction}"
          values={{ transaction: transactionText }}
        />
      </ProgressButton>
    );
  };

  const buttonColor = getButtonColor();

  return (
    <Fragment>
      <div className="flex vertical-margin-20 center-align">
        <div className="view-invoice-button-container">{getLeftContent()}</div>
        <div className="print-download-button-container flex">
          <button
            type="button"
            className="button-container flex center-align"
            onClick={() => onAction(PDF_ACTION.DOWNLOAD)}
            data-cy={`invoice-summary-download`}
            aria-label={`Download ${transactionText}`}
          >
            <Download color={buttonColor} width={24} height={24} />
          </button>
          <div className="buttons-separator" />
          <button
            type="button"
            className="button-container flex center-align"
            onClick={() => onAction(PDF_ACTION.PRINT)}
            data-cy={`invoice-summary-print`}
            aria-label={`Print ${transactionText}`}
          >
            <Print color={buttonColor} width={24} height={24} />
          </button>
        </div>
      </div>
      <style jsx>{`
        .vertical-margin-20 {
          margin-top: 20px;
          ${includeBottomMargin ? 'margin-bottom: 20px;' : ''}
        }
        .center-align {
          align-items: center;
        }
        .view-invoice-button-container {
          display: flex;
          align-items: center;
          flex-grow: 1;
        }
        .print-download-button-container {
          justify-content: flex-end;
          flex-grow: 2;
        }
        .button-container {
          border: none;
          background: transparent;
          padding: 0;
          cursor: pointer;
          position: relative;
          &:hover,
          &:focus {
            background-color: ${colors.gray06};
          }
        }
        .buttons-separator {
          width: 16px;
        }
      `}</style>
    </Fragment>
  );
};

// Clean mapStateToProps that extracts exactly what we need
function mapStateToProps(state: RootState) {
  return {
    sale: state.sale,
    companyInfo: state.companyInfo,
    insight: state.insight,
    featureFlags: state.featureFlags,
    ixp: state.ixp,
    invoiceDocument: state.invoiceDocument,
    config: state.config,
    payment: state.payment,
    isBatchPaymentsEnabled: isBatchPaymentsEnabled(state),
  };
}

export default connect(mapStateToProps, {
  fetchPDFDocument,
  downloadPDFDocument,
  showModal: modalActions.show,
})(PreviewCard);
